import React, { useContext, useState } from "react";
import { themeContext } from "../utils/themeContext";
import { motion } from "framer-motion";
import {
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaPaperPlane,
  FaUser,
  FaComment,
} from "react-icons/fa";

const ContactMe = () => {
  const { isDarkMode } = useContext(themeContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(event.target);
    formData.append("access_key", "1581ecf8-bb11-4782-826e-824e16d7beaf");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(Object.fromEntries(formData)),
      }).then((res) => res.json());

      if (res.success) {
        setSubmitStatus({
          success: true,
          message: "Message sent successfully!",
        });
        event.target.reset();
      } else {
        setSubmitStatus({
          success: false,
          message: "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "An error occurred. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`pt-16 md:pt-20 px-6 md:px-10 py-12 min-h-screen flex flex-col items-center justify-start ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      {/* Section Heading */}
      <motion.div
        className='w-full max-w-4xl'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className='text-4xl md:text-5xl font-extrabold mb-6 text-center'>
          <span className=''>Get in Touch</span>
        </h1>

        {/* Profile Section */}
        <div className='flex flex-col md:flex-row items-center justify-center mb-12 gap-8'>
          {/* Profile Image */}
          <motion.img
            src='https://res.cloudinary.com/dksf1htal/image/upload/v1745897332/my-image_taj9bg.jpg'
            alt='Profile'
            className='w-40 h-40 rounded-full shadow-xl border-2 border-red-300 object-cover hover:scale-105 transition-transform duration-300'
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          <div className='text-center md:text-left'>
            {/* Name */}
            <h2 className='text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-300 to-red-400'>
              Taniya Kamboj
            </h2>
            {/* Short About Me */}
            <p
              className={`text-lg mt-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-700"
              }`}
            >
              Full Stack Developer | MERN | Passionate about building scalable
              and innovative applications.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <motion.form
          onSubmit={onSubmit}
          className={`p-8 rounded-2xl shadow-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {submitStatus && (
            <div
              className={`p-4 mb-6 rounded-lg ${
                submitStatus.success
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              } ${
                isDarkMode
                  ? submitStatus.success
                    ? "bg-green-900 text-green-100"
                    : "bg-red-900 text-red-100"
                  : ""
              }`}
            >
              {submitStatus.message}
            </div>
          )}

          <div className='space-y-6'>
            <div>
              <label
                htmlFor='name'
                className='block mb-2 font-medium flex items-center gap-2'
              >
                <FaUser className='text-red-300' />
                Your Name
              </label>
              <input
                type='text'
                id='name'
                name='name'
                required
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-300 transition-all ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-300"
                }`}
                placeholder='Enter your name'
              />
            </div>

            <div>
              <label
                htmlFor='email'
                className='block mb-2 font-medium flex items-center gap-2'
              >
                <FaEnvelope className='text-red-300' />
                Email Address
              </label>
              <input
                type='email'
                id='email'
                name='email'
                required
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-300 transition-all ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-300"
                }`}
                placeholder='Enter your email'
              />
            </div>

            <div>
              <label
                htmlFor='message'
                className='block mb-2 font-medium flex items-center gap-2'
              >
                <FaComment className='text-red-300' />
                Your Message
              </label>
              <textarea
                id='message'
                name='message'
                rows='5'
                required
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-300 transition-all ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-300"
                }`}
                placeholder='Enter your message here...'
              ></textarea>
            </div>

            <motion.button
              type='submit'
              className={`w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                isSubmitting ? "bg-red-300" : "bg-red-300 hover:bg-red-400"
              } text-white transition-colors`}
              disabled={isSubmitting}
              whileHover={!isSubmitting ? { scale: 1.02 } : {}}
              whileTap={!isSubmitting ? { scale: 0.98 } : {}}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Send Message
                </>
              )}
            </motion.button>
          </div>
        </motion.form>

        {/* Contact Icons */}
        <div className='flex justify-center gap-6 mt-12'>
          {/* <motion.a
            href='tel:+7417012438'
            className={`p-4 rounded-full ${
              isDarkMode
                ? "bg-gray-800 hover:bg-gray-700"
                : "bg-white hover:bg-gray-200"
            } shadow-md`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaPhone className='text-red-300 text-xl' />
          </motion.a> */}
          {/* <motion.a
            href='mailto:taniyakamboj184@gmail.com'
            className={`p-4 rounded-full ${
              isDarkMode
                ? "bg-gray-800 hover:bg-gray-700"
                : "bg-white hover:bg-gray-200"
            } shadow-md`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaEnvelope className='text-red-300 text-xl' />
          </motion.a> */}
          {/* <motion.a
            href='https://wa.me/7417012438'
            className={`p-4 rounded-full ${
              isDarkMode
                ? "bg-gray-800 hover:bg-gray-700"
                : "bg-white hover:bg-gray-200"
            } shadow-md`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaWhatsapp className='text-red-300 text-xl' />
          </motion.a> */}
        </div>
      </motion.div>
    </div>
  );
};

export default ContactMe;
